import { Popover, Progress } from "antd";
import { DateFormat } from "../../enums/dateTimeFormat";
import moment from "moment-timezone";
import { getPercentage } from "../../utils/mathUtils";
import { fetchFormattedDate } from "../../utils/dateUtils";
import * as Yup from "yup";
import { PlacementPositions } from "../../enums/PlacementPositionsEnums";
import { EllipsisOutlined } from "@ant-design/icons";
import lockIcon from "../../assets/images/lockIcon.svg";
import unlockIcon from "../../assets/images/unlockIcon.svg";
import styles from "./TicketUpload.module.scss";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { TicketLockStatusParams } from "../../services/ticketUpload.service";

interface ColumnParamsType {
  updateTicketLockStatus?: UseMutateAsyncFunction<
    any,
    Error,
    TicketLockStatusParams,
    unknown
  >;
}

export const ticketViewConstants = {
  EMPTY_TABLE_MSG: "No Tickets are uploaded yet",
  EMPTY_SEARCH_MSG: "No Tickets found",
  SEARCH_FORMAT_ERROR_MSG: "Invalid Search Format",
  UPLOAD_TKT_BUTTON_TEXT: "+ Upload Ticket",
  FILTER_BUTTON_TEXT: "Filter",
  SEARCH_PLACEHOLDER: "Search",
  SEARCH_ICON_ALT: "click to search",
  FILTER_ICON_ALT: "open filter panel",
  SEARCH_ERROR_TXT: "Invalid search format",
  REVIEW_ACCESS_ERROR: "Access required for submission",
};

export const TABLE_PAGESIZE_OPTIONS = [10, 25, 50, 100];

export const FilterButtonStyles = {
  color: "#586370",
  backgroundColor: "white",
  border: "1px solid #e1e1e1",
  borderRadius: "1px",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: "400",
  fontSize: "14px",
  lineHeight: "22px",
  textShadow: "none",
  borderRadius: "8px",
};

export const fieldNames = {
  SEARCH: "searchTicket",
};

const ellipsisConstants = {
  lockLabel: "Lock",
  unlockLabel: "Unlock",
};

const lockComponent = (
  <>
    <img src={lockIcon} />
    {ellipsisConstants.lockLabel}
  </>
);

const unlockComponent = (
  <>
    <img src={unlockIcon} />
    {ellipsisConstants.unlockLabel}
  </>
);

export const getTicketColumns = (params: ColumnParamsType) => [
  {
    title: "Upload Date",
    sorter: false,
    width: "140px",
    key: "uploadDate",
    dataIndex: "createdAt",
    render: (createdAt: number) => {
      return (
        <span className="table-font-mobile">
          {createdAt && fetchFormattedDate(createdAt)}
        </span>
      );
    },
  },
  {
    title: "Customer",
    sorter: false,
    width: "160px",
    key: "contactName",
    dataIndex: "companyName",
    render: (companyName, record) => {
      return <span className="table-font-mobile">{companyName}</span>;
    },
  },
  {
    title: "Job Id",
    sorter: false,
    width: "160px",
    key: "jobNumber",
    dataIndex: "jobNumber",
    render: (jobNumber, record) => {
      return <span className="table-font-mobile">{jobNumber}</span>;
    },
  },
  {
    title: "Job Name",
    sorter: false,
    width: "240px",
    key: "jobName",
    dataIndex: "jobName",
    render: (jobName, record) => {
      return <span className="table-font-mobile">{jobName}</span>;
    },
  },
  {
    title: "Order",
    sorter: false,
    width: "140px",
    key: "orderId",
    dataIndex: "materialName",
    render: (materialName, record) => {
      return <span className="table-font-mobile">{materialName}</span>;
    },
  },
  {
    title: "Truck No",
    sorter: false,
    width: "100px",
    key: "truckNumber",
    dataIndex: "truckId",
    render: (truckId, record) => {
      return <span className="table-font-mobile">{truckId}</span>;
    },
  },
  {
    title: "Tickets",
    sorter: false,
    width: "7%",
    key: "ticketCount",
    dataIndex: "ticketCount",
    render: (ticketCount, record) => {
      return (
        <span className="table-font-mobile">
          {record?.ticketsMoved}/{record?.totalTickets}
        </span>
      );
    },
  },
  {
    title: "Status",
    sorter: false,
    width: "13%",
    key: "status",
    dataIndex: "status",
    render: (status, record) => {
      return (
        <Progress
          percent={getPercentage(
            String(record?.ticketsMoved),
            String(record?.totalTickets),
          )}
          strokeColor={"#28E648"}
          status={"normal"}
        />
      );
    },
  },
  {
    title: "",
    key: "actions",
    dataIndex: "actions",
    width: "50px",
    fixed: "right",
    render: (_, record) => {
      return (
        <Popover
          placement={PlacementPositions.BOTTOM}
          content={
            <div
              className={styles.actionIcon}
              onClick={async (e) => {
                e.stopPropagation();

                await params?.updateTicketLockStatus({
                  id: record?.id,
                  locked: !record?.locked,
                });
              }}
            >
              {record?.locked === true ? unlockComponent : lockComponent}
            </div>
          }
        >
          <EllipsisOutlined
            style={{ fontSize: "30px", color: "#C3C8CD", cursor: "pointer" }}
            className="action-ellipsis-button"
            onClick={(e) => e.stopPropagation()}
          />
        </Popover>
      );
    },
  },
];

export const validationMessages = {
  search: "Maximum 50 characters allowed.",
};

export const searchValidationSchema = Yup.object({
  [fieldNames.SEARCH]: Yup.string().max(50, validationMessages.search),
});

export const FILTER_FIELD_NAMES_TO_COUNT = [
  "companyIds",
  "truckIds",
  "orderIds",
  "jobNumbers",
  "jobNames",
  "fromDate",
] as const;
